import React, { createContext, useCallback, useContext, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Catalog layout shared by the operators marketplace and a project's workflows.
 *
 * Type groups flow newspaper-style into as many columns as fit (each at least
 * 20rem unless the page asks for wider), so small groups sit side by side instead
 * of each claiming a full row. Columns are sized from the container's own width,
 * so a docked drawer or the chat column drops a column without any breakpoint
 * logic. Inline styles rather than Tailwind: multi-column is not used elsewhere
 * and must not depend on the utility being generated.
 */
// A group is never split across columns, so its header always sits with its cards.
const groupStyle: React.CSSProperties = { breakInside: 'avoid' };

/**
 * Whitespace between and within groups. `compact` suits small cards packed beside
 * other content; `roomy` suits a page of large cards with nothing else competing.
 * Class strings are written out whole so Tailwind can see them.
 */
export type CatalogSpacing = 'compact' | 'roomy';

const SPACING = {
    compact: { columnGap: '1.5rem', group: 'mb-5', cards: 'gap-3 p-3' },
    roomy: { columnGap: '2rem', group: 'mb-8', cards: 'gap-4 p-4' },
} as const;

// Set once on CatalogColumns and read by each group, so call sites pass it once.
const SpacingContext = createContext<CatalogSpacing>('compact');

interface CatalogColumnsProps {
    /** Narrowest a column may get before one is dropped. Wider means fewer, roomier columns. */
    minColumnWidth?: string;
    spacing?: CatalogSpacing;
    children: React.ReactNode;
}

export const CatalogColumns: React.FC<CatalogColumnsProps> = ({ minColumnWidth = '20rem', spacing = 'compact', children }) => (
    <SpacingContext.Provider value={spacing}>
        <div className="m-0 w-full" style={{ columns: minColumnWidth, columnGap: SPACING[spacing].columnGap }}>{children}</div>
    </SpacingContext.Provider>
);

interface CatalogGroupProps {
    label: string;
    count: number;
    collapsed: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

/** One type group: a header that folds it, and its cards stacked beneath. */
export const CatalogGroup: React.FC<CatalogGroupProps> = ({ label, count, collapsed, onToggle, children }) => {
    const spacing = SPACING[useContext(SpacingContext)];
    return (
        <div className={`m-0 w-full ${spacing.group} text-left bg-surface/70 border border-line rounded-xl overflow-hidden`} style={groupStyle}>
            <h3 className="m-0">
                {/* The whole header row toggles, so the target is the panel's full width. */}
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={!collapsed}
                    className="w-full flex items-center gap-2 bg-transparent border-none px-4 py-3 m-0 cursor-pointer text-left text-fg hover:text-accent-fg transition-colors"
                >
                    <ExpandMoreIcon fontSize="small" className={`m-0 text-fg-2 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    <span className="m-0 min-w-0 flex-1 text-xs font-mono font-bold uppercase tracking-wider">{label}</span>
                    <span className="m-0 shrink-0 text-[10px] font-mono font-bold text-accent-fg bg-accent/10 border border-accent-fg/30 rounded-full px-2 py-0.5 leading-none">{count}</span>
                </button>
            </h3>
            {!collapsed && (
                <div className={`flex flex-col ${spacing.cards} m-0 w-full border-t border-line`}>
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Which groups the user has folded away. Everything starts expanded. Keys are the
 * caller's: a page with two catalogs prefixes them so a shared label folds
 * independently in each.
 */
export const useCollapsedGroups = () => {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const toggle = useCallback((key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);
    return { isCollapsed: (key: string) => collapsed.has(key), toggle };
};
